import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import type {
  BundleInfo,
  NoNeedEvent,
  DownloadEvent,
  LatestVersion,
  AppReadyEvent,
  BuiltinVersion,
  UpdateFailedEvent,
  CurrentBundleResult,
  DownloadFailedEvent,
  UpdateAvailableEvent,
  DownloadCompleteEvent,
} from '@capgo/capacitor-updater';

import { CONFIG } from 'src/environments/environment';

import { withTimeout } from '../promise-timeout';

// ----------------------------------------------------------------------

// Timeout configuration for service operations (in milliseconds)
const SERVICE_TIMEOUTS = {
  GET_CURRENT: 5000, // 5 seconds for getCurrentVersion
  GET_NATIVE: 5000, // 5 seconds for getNativeVersion
  CHECK_UPDATE: 15000, // 15 seconds for checking updates (network call)
  GET_LATEST: 15000, // 15 seconds for getLatest API call
  DOWNLOAD: 180000, // 3 minutes for downloading bundles
  INSTALL: 10000, // 10 seconds for installing bundle
  RESET: 10000, // 10 seconds for reset operations
  LIST_BUNDLES: 5000, // 5 seconds for listing bundles
  DELETE_BUNDLE: 5000, // 5 seconds for deleting bundles
} as const;

// ----------------------------------------------------------------------

// Custom types for better TypeScript support
export interface UpdateInfo {
  hasUpdate: boolean;
  latestVersion?: LatestVersion;
  currentVersion: CurrentBundleResult;
  changelog?: string;
}

export interface DownloadProgress {
  percent: number;
  bundleId: string;
  version: string;
  bytesDownloaded?: number;
  totalBytes?: number;
  estimatedTimeRemaining?: number;
}

export interface UpdateHistory {
  version: string;
  bundleId: string;
  installedAt: Date;
  rollbackAvailable: boolean;
  status: 'active' | 'available' | 'failed';
}

// Type guards for listener callbacks
type DownloadChangeListener = (event: DownloadEvent) => void;
type UpdateAvailableListener = (event: UpdateAvailableEvent) => void;
type DownloadCompleteListener = (event: DownloadCompleteEvent) => void;
type DownloadFailedListener = (event: DownloadFailedEvent) => void;
type UpdateFailedListener = (event: UpdateFailedEvent) => void;
type NoNeedUpdateListener = (event: NoNeedEvent) => void;
type AppReadyListener = (event: AppReadyEvent) => void;

// ----------------------------------------------------------------------

class CapacitorUpdaterService {
  private listeners: Map<string, PluginListenerHandle> = new Map();

  private downloadStartTime?: number;

  private downloadedBytes: number = 0;

  // Caching mechanism to prevent rate limiting
  private lastUpdateCheck?: {
    timestamp: number;
    result: UpdateInfo;
  };

  // Minimum time between update checks (in milliseconds)
  // Default: 10 seconds to prevent 429 errors
  private readonly MIN_CHECK_INTERVAL = 10 * 1000; // 10 seconds

  // Cache time-to-live (in milliseconds)
  // How long to consider cached results valid
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Call tracking for debugging
  private calls = 0;

  private nextAllowedAt = 0;

  /**
   * Initialize the updater service
   * Should be called on app startup
   */
  // eslint-disable-next-line class-methods-use-this
  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Capacitor Updater: Skipping initialization on web platform');
      return;
    }

    try {
      await CapacitorUpdater.notifyAppReady();
    } catch (error) {
      console.error('Failed to initialize Capacitor Updater:', error);
      throw error;
    }
  }

  /**
   * Get current bundle information
   */
  // eslint-disable-next-line class-methods-use-this
  async getCurrentVersion(): Promise<CurrentBundleResult> {
    if (!Capacitor.isNativePlatform()) {
      // Return a default version for web
      return {
        bundle: {
          id: 'web',
          version: CONFIG.VITE_APP_VERSION,
          downloaded: '',
          checksum: '',
          status: 'success' as any,
        },
        native: CONFIG.VITE_APP_VERSION,
        message: '',
      } as CurrentBundleResult;
    }
    return withTimeout(
      CapacitorUpdater.current(),
      SERVICE_TIMEOUTS.GET_CURRENT,
      'getCurrentVersion timeout'
    );
  }

  /**
   * Get the native app version
   */
  // eslint-disable-next-line class-methods-use-this
  async getNativeVersion(): Promise<BuiltinVersion> {
    if (!Capacitor.isNativePlatform()) {
      // Return a default version for web
      return {
        version: CONFIG.VITE_APP_VERSION,
      } as BuiltinVersion;
    }
    return withTimeout(
      CapacitorUpdater.getBuiltinVersion(),
      SERVICE_TIMEOUTS.GET_NATIVE,
      'getNativeVersion timeout'
    );
  }

  /**
   * Get time remaining until next allowed check (in milliseconds)
   * Returns 0 if check is allowed now
   */
  getTimeUntilNextCheck(): number {
    if (!this.lastUpdateCheck) {
      return 0;
    }

    const timeSinceLastCheck = Date.now() - this.lastUpdateCheck.timestamp;
    const timeRemaining = this.MIN_CHECK_INTERVAL - timeSinceLastCheck;

    return Math.max(0, timeRemaining);
  }

  /**
   * Check if an update check is currently allowed (not rate limited)
   */
  canCheckForUpdate(): boolean {
    return this.getTimeUntilNextCheck() === 0;
  }

  /**
   * Clear the update check cache
   * Use this when you want to force a fresh check regardless of timing
   */
  clearUpdateCache(): void {
    this.lastUpdateCheck = undefined;
  }

  /**
   * Log API call for debugging and monitoring
   * Logs every 5th call to avoid console spam
   */
  private logCall(phase: string): void {
    this.calls += 1;
    if (this.calls % 5 === 1) {
      const nextAllowedDate = new Date(this.nextAllowedAt).toISOString();
      console.log(`[Updater] calls=${this.calls} phase=${phase} nextAllowed=${nextAllowedDate}`);
    }
  }

  /**
   * Check for available updates with built-in caching and rate limiting
   * @param forceCheck - If true, bypasses cache and forces a fresh check (still respects rate limit)
   * @param useCache - If true and cache exists, returns cached result without checking rate limit
   */
  async checkForUpdate(forceCheck = false, useCache = true): Promise<UpdateInfo> {
    // Skip on non-native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log(`Platform ${Capacitor.getPlatform()} - skipping update check`);
      return {
        hasUpdate: false,
        latestVersion: undefined,
        currentVersion: {
          bundle: {
            id: 'web',
            version: CONFIG.VITE_APP_VERSION,
            downloaded: '',
            checksum: '',
            status: 'success' as any,
          },
          native: CONFIG.VITE_APP_VERSION,
          message: '',
        } as CurrentBundleResult,
        changelog: undefined,
      };
    }

    // Return cached result if available and not forcing check
    if (!forceCheck && useCache && this.lastUpdateCheck) {
      const cacheAge = Date.now() - this.lastUpdateCheck.timestamp;

      if (cacheAge < this.CACHE_TTL) {
        console.log(`Using cached update check result (${Math.round(cacheAge / 1000)}s old)`);
        return this.lastUpdateCheck.result;
      }
    }

    // Check rate limiting
    const timeUntilNext = this.getTimeUntilNextCheck();
    if (timeUntilNext > 0 && !forceCheck) {
      const error = new Error(
        `Rate limit: Please wait ${Math.ceil(timeUntilNext / 1000)} seconds before checking again`
      );
      (error as any).code = 'RATE_LIMIT';
      (error as any).retryAfter = timeUntilNext;

      // Return cached result if available
      if (this.lastUpdateCheck) {
        console.log('Rate limited - returning cached result');
        return this.lastUpdateCheck.result;
      }

      throw error;
    }

    try {
      const current = await CapacitorUpdater.current();

      let latest;
      let hasUpdate = false;

      try {
        // Log before making network call
        this.logCall('pre');

        latest = await withTimeout(
          CapacitorUpdater.getLatest({}),
          SERVICE_TIMEOUTS.GET_LATEST,
          'getLatest API call timeout'
        );

        // Log after successful network call
        this.logCall('post');

        // Parse version strings for comparison
        hasUpdate = latest.version !== current.bundle.version;
      } catch (error: any) {
        // Handle 429 rate limiting errors specifically
        const errorMessage = error?.message?.toLowerCase() || '';
        const errorCode = error?.code?.toLowerCase() || '';
        const statusCode = error?.status || error?.statusCode;

        if (
          statusCode === 429 ||
          errorMessage.includes('rate limit') ||
          errorMessage.includes('too many requests')
        ) {
          console.warn('Capgo API rate limit reached (429). Please wait before checking again.');
          const rateLimitError = new Error(
            'Update server rate limit reached. Please try again later.'
          );
          (rateLimitError as any).code = 'RATE_LIMIT_SERVER';
          (rateLimitError as any).originalError = error;

          // Return cached result if available
          if (this.lastUpdateCheck) {
            console.log('429 error - returning cached result');
            return this.lastUpdateCheck.result;
          }

          throw rateLimitError;
        }

        // Handle various "no update" scenarios as normal cases, not errors
        const noUpdateIndicators = [
          'no_new_version_available',
          'no new version available',
          'already up to date',
          'no update available',
          'latest version installed',
        ];

        const isNoUpdateError = noUpdateIndicators.some(
          (indicator) => errorMessage.includes(indicator) || errorCode.includes(indicator)
        );

        if (isNoUpdateError) {
          // No update available - this is normal, not an error
          console.log('No new version available - app is up to date');
          const result: UpdateInfo = {
            hasUpdate: false,
            latestVersion: undefined,
            currentVersion: current,
            changelog: undefined,
          };

          // Cache the result
          const now = Date.now();
          this.lastUpdateCheck = {
            timestamp: now,
            result,
          };

          // Update next allowed check time
          this.nextAllowedAt = now + this.MIN_CHECK_INTERVAL;

          return result;
        }
        // Re-throw if it's a different error
        throw error;
      }

      const result: UpdateInfo = {
        hasUpdate,
        latestVersion: hasUpdate ? latest : undefined,
        currentVersion: current,
        changelog: latest?.message, // Capgo can include changelog in the message field
      };

      // Cache the successful result
      const now = Date.now();
      this.lastUpdateCheck = {
        timestamp: now,
        result,
      };

      // Update next allowed check time
      this.nextAllowedAt = now + this.MIN_CHECK_INTERVAL;

      return result;
    } catch (error) {
      console.error('Failed to check for updates:', error);
      throw error;
    }
  }

  /**
   * Download an update bundle with progress tracking
   */
  async downloadUpdate(
    version: LatestVersion,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<BundleInfo> {
    // Skip on non-native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot download updates on non-native platform');
      throw new Error('Updates are only available on mobile devices');
    }
    this.downloadStartTime = Date.now();
    this.downloadedBytes = 0;

    // Set up progress listener
    if (onProgress) {
      const listener: DownloadChangeListener = (event: DownloadEvent) => {
        const progress: DownloadProgress = {
          percent: event.percent,
          bundleId: event.bundle.id,
          version: event.bundle.version,
        };

        // Calculate download speed and estimated time
        if (this.downloadStartTime) {
          const elapsedSeconds = (Date.now() - this.downloadStartTime) / 1000;
          const bytesPerSecond = this.downloadedBytes / elapsedSeconds;

          if (event.percent > 0 && bytesPerSecond > 0) {
            const totalBytes = (this.downloadedBytes * 100) / event.percent;
            const remainingBytes = totalBytes - this.downloadedBytes;
            const estimatedSeconds = remainingBytes / bytesPerSecond;

            progress.bytesDownloaded = this.downloadedBytes;
            progress.totalBytes = totalBytes;
            progress.estimatedTimeRemaining = estimatedSeconds;
          }
        }

        onProgress(progress);
      };

      await this.addEventListener('download', listener);
    }

    try {
      const bundle = await withTimeout(
        CapacitorUpdater.download({
          url: version.url || '',
          version: version.version,
        }),
        SERVICE_TIMEOUTS.DOWNLOAD,
        'Bundle download timeout'
      );

      // Clean up listener
      if (onProgress) {
        await this.removeEventListener('download');
      }

      return bundle;
    } catch (error) {
      // Clean up listener on error
      if (onProgress) {
        await this.removeEventListener('download');
      }
      throw error;
    }
  }

  /**
   * Install an update immediately (app will reload)
   */
  // eslint-disable-next-line class-methods-use-this
  async installUpdate(bundleId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot install updates on non-native platform');
      throw new Error('Updates are only available on mobile devices');
    }
    await withTimeout(
      CapacitorUpdater.set({ id: bundleId }),
      SERVICE_TIMEOUTS.INSTALL,
      'Bundle installation timeout'
    );
    // Note: App will reload automatically after this
  }

  /**
   * Set next update to be applied on next app launch
   */
  // eslint-disable-next-line class-methods-use-this
  async setNextUpdate(bundleId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot set next update on non-native platform');
      throw new Error('Updates are only available on mobile devices');
    }
    await CapacitorUpdater.next({ id: bundleId });
  }

  /**
   * Rollback to a previous version
   */
  // eslint-disable-next-line class-methods-use-this
  async rollbackVersion(bundleId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot rollback version on non-native platform');
      throw new Error('Rollback is only available on mobile devices');
    }
    await CapacitorUpdater.set({ id: bundleId });
    // Note: App will reload automatically after this
  }

  /**
   * Reset to the built-in native version
   */
  // eslint-disable-next-line class-methods-use-this
  async resetToBuiltin(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot reset to builtin on non-native platform');
      throw new Error('Reset is only available on mobile devices');
    }
    await withTimeout(CapacitorUpdater.reset(), SERVICE_TIMEOUTS.RESET, 'Reset to builtin timeout');
    // Note: App will reload automatically after this
  }

  /**
   * Get list of available bundles for rollback
   */
  // eslint-disable-next-line class-methods-use-this
  async getAvailableBundles(): Promise<BundleInfo[]> {
    if (!Capacitor.isNativePlatform()) {
      return []; // No bundles on web
    }
    const result = await withTimeout(
      CapacitorUpdater.list(),
      SERVICE_TIMEOUTS.LIST_BUNDLES,
      'List bundles timeout'
    );
    return result.bundles;
  }

  /**
   * Delete a specific bundle
   */
  // eslint-disable-next-line class-methods-use-this
  async deleteBundle(bundleId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot delete bundles on non-native platform');
      throw new Error('Bundle management is only available on mobile devices');
    }
    await withTimeout(
      CapacitorUpdater.delete({ id: bundleId }),
      SERVICE_TIMEOUTS.DELETE_BUNDLE,
      'Delete bundle timeout'
    );
  }

  /**
   * Get update history from local storage
   */
  // eslint-disable-next-line class-methods-use-this
  getUpdateHistory(): UpdateHistory[] {
    const history = localStorage.getItem('capacitor-updater-history');
    return history ? JSON.parse(history) : [];
  }

  /**
   * Save update to history
   */
  // eslint-disable-next-line class-methods-use-this
  async saveUpdateToHistory(bundle: BundleInfo, status: UpdateHistory['status']): Promise<void> {
    const history = this.getUpdateHistory();

    // Check if bundle already exists in history
    const existingIndex = history.findIndex((h) => h.bundleId === bundle.id);

    const newEntry: UpdateHistory = {
      version: bundle.version,
      bundleId: bundle.id,
      installedAt: new Date(),
      rollbackAvailable: status === 'active' || status === 'available',
      status,
    };

    if (existingIndex >= 0) {
      history[existingIndex] = newEntry;
    } else {
      history.unshift(newEntry);
    }

    // Keep only last 10 entries
    const trimmedHistory = history.slice(0, 10);
    localStorage.setItem('capacitor-updater-history', JSON.stringify(trimmedHistory));
  }

  /**
   * Format bytes to human readable string
   */
  // eslint-disable-next-line class-methods-use-this
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Format seconds to human readable time
   */
  // eslint-disable-next-line class-methods-use-this
  formatTime(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)} seconds`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
    return `${Math.round(seconds / 3600)} hours`;
  }

  /**
   * Add event listener
   */
  async addEventListener(eventName: 'download', listener: DownloadChangeListener): Promise<void>;
  async addEventListener(
    eventName: 'updateAvailable',
    listener: UpdateAvailableListener
  ): Promise<void>;
  async addEventListener(
    eventName: 'downloadComplete',
    listener: DownloadCompleteListener
  ): Promise<void>;
  async addEventListener(
    eventName: 'downloadFailed',
    listener: DownloadFailedListener
  ): Promise<void>;
  async addEventListener(eventName: 'updateFailed', listener: UpdateFailedListener): Promise<void>;
  async addEventListener(eventName: 'noNeedUpdate', listener: NoNeedUpdateListener): Promise<void>;
  async addEventListener(eventName: 'appReady', listener: AppReadyListener): Promise<void>;
  async addEventListener(eventName: string, listener: any): Promise<void> {
    // Remove existing listener if any
    if (this.listeners.has(eventName)) {
      await this.removeEventListener(eventName);
    }

    // Add new listener
    const handle = await CapacitorUpdater.addListener(eventName as any, listener);
    this.listeners.set(eventName, handle);
  }

  /**
   * Remove event listener
   */
  async removeEventListener(eventName: string): Promise<void> {
    const handle = this.listeners.get(eventName);
    if (handle) {
      await handle.remove();
      this.listeners.delete(eventName);
    }
  }

  /**
   * Remove all event listeners
   */
  async removeAllListeners(): Promise<void> {
    const entries = Array.from(this.listeners.entries());
    await Promise.all(
      entries.map(async ([eventName, handle]) => {
        await handle.remove();
        this.listeners.delete(eventName);
      })
    );
  }

  /**
   * Compare two semantic version strings
   * Returns true if version1 >= version2
   * @param version1 - First version string (e.g., "2.9.0")
   * @param version2 - Second version string (e.g., "2.8.0")
   */
  // eslint-disable-next-line class-methods-use-this
  private compareVersions(version1: string, version2: string): boolean {
    // Normalize versions by removing pre-release/build metadata
    const normalize = (version: string): number[] => {
      const coreVersion = version.trim().split(/[-+]/)[0];
      const parts = coreVersion.split('.');

      // Ensure we have exactly 3 parts (major.minor.patch)
      while (parts.length < 3) {
        parts.push('0');
      }

      // Take only the first 3 parts and convert to numbers
      return parts.slice(0, 3).map((part) => {
        const num = parseInt(part, 10);
        return Number.isNaN(num) ? 0 : num;
      });
    };

    const v1Parts = normalize(version1);
    const v2Parts = normalize(version2);

    // Compare each part (major, minor, patch)
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 3; i++) {
      if (v1Parts[i] > v2Parts[i]) {
        return true;
      }
      if (v1Parts[i] < v2Parts[i]) {
        return false;
      }
    }

    // Versions are equal
    return true;
  }

  /**
   * Check if native version should be used instead of current bundle
   * Returns true if native version >= current bundle version
   * This helps detect when a new app store version has been installed
   * @returns Object with shouldReset flag and version information
   */
  async shouldUseNativeVersion(): Promise<{
    shouldReset: boolean;
    nativeVersion: string;
    bundleVersion: string;
    isRunningNative: boolean;
  }> {
    if (!Capacitor.isNativePlatform()) {
      return {
        shouldReset: false,
        nativeVersion: CONFIG.VITE_APP_VERSION,
        bundleVersion: CONFIG.VITE_APP_VERSION,
        isRunningNative: false,
      };
    }

    try {
      // Get current bundle and native version information with timeout
      const [currentBundle, nativeInfo] = await Promise.all([
        withTimeout(
          CapacitorUpdater.current(),
          SERVICE_TIMEOUTS.GET_CURRENT,
          'getCurrentBundle timeout in shouldUseNativeVersion'
        ),
        withTimeout(
          CapacitorUpdater.getBuiltinVersion(),
          SERVICE_TIMEOUTS.GET_NATIVE,
          'getNativeVersion timeout in shouldUseNativeVersion'
        ),
      ]);

      const bundleVersion = currentBundle.bundle.version;
      const nativeVersion = nativeInfo.version;

      // Check if we're already running the native/builtin version
      // The builtin bundle ID is typically 'builtin' or matches the native version
      const isRunningNative =
        currentBundle.bundle.id === 'builtin' ||
        currentBundle.bundle.id === nativeVersion ||
        bundleVersion === nativeVersion;

      // Determine if we should reset to native
      // Reset if: native version >= bundle version AND we're not already on native
      const shouldReset = !isRunningNative && this.compareVersions(nativeVersion, bundleVersion);

      return {
        shouldReset,
        nativeVersion,
        bundleVersion,
        isRunningNative,
      };
    } catch (error) {
      console.error('Error checking if native version should be used:', error);
      return {
        shouldReset: false,
        nativeVersion: '0.0.0',
        bundleVersion: '0.0.0',
        isRunningNative: false,
      };
    }
  }
}

// Export singleton instance
export const capacitorUpdaterService = new CapacitorUpdaterService();
